import {
  Box,
  Button,
  createStyles,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import ReportOutageBoltIcon from '../../../../static/icon-bolt.svg';
import HelpIcon from '../../../../static/outageIconsV2/help.svg';
import OutageStatusGreen from '../../../../static/outageIconsV2/outage_status_green.svg';
import colors from '../../../../themes/main-colors';
import theme from '../../../../themes/theme';
import Backdrop from '../../../backdrop/Backdrop';
import PGEButton from '../../../buttons';
import { AccountWithOutageInfo } from '../../types';
import { CustomDialog } from '../common/custom-dialog';
import { useMyPowerIsOn } from './useMyPowerIsOn';

const useStyles = makeStyles(() =>
  createStyles({
    dialogContent: {
      scrollbarWidth: 'none',
      overflowY: 'auto',
      maxHeight: '600px',
    },
    contentSubText: {
      display: 'flex',
      flexDirection: 'column',
    },
    contentSubTextContainer: {
      background: colors.white,
    },
    outageIcon: {
      fontSize: 40,
      color: colors.white,
      display: 'flex',
    },
    textMatterLight: {
      fontSize: 16,
    },
    actions: {
      padding: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      background: colors.riverBlue,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
      },
    },
    cancelBtn: {
      color: colors.sparkBlue,
      fontWeight: 600,
      background: 'transparent',
      boxShadow: 'none',
      fontSize: 18,
      width: '50%',
      '&:hover': {
        background: 'transparent',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    lookupBtn: {
      color: '#fff',
      width: '50%',
      fontWeight: 600,
      borderRadius: 24,
      fontSize: 18,
      background: colors.sparkBlue,
      padding: '10px 32px',
      '&:hover': {
        background: colors.sparkBlue,
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginBottom: 15,
      },
    },
    subTextHeader: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
    },
    subTextHeaderTitle: {
      marginTop: '1.5rem',
      textAlign: 'center',
    },
    helpIcon: {
      transform: 'scale(2)',
      color: colors.white,
    },
    greenCheckIcon: {
      transform: 'scale(1.5)',
    },
  }),
);

type MyPowerIsOnProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAccountForOutage: AccountWithOutageInfo | undefined;
};

export function MyPowerIsOn({
  open,
  setOpen,
  selectedAccountForOutage,
}: MyPowerIsOnProps) {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { submitPowerIsOn, isLoading, isSuccessful } = useMyPowerIsOn();

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    submitPowerIsOn(
      selectedAccountForOutage?.accountNumber,
      selectedAccountForOutage?.encryptedPersonId,
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={t('REPORT_OUTAGE_TITLE')}
      titleIcon={<ReportOutageBoltIcon className={classes.outageIcon} />}
    >
      {isLoading && <Backdrop forceOpen />}
      <Box className={classes.dialogContent}>
        <Box className={classes.contentSubText}>
          <Box
            paddingX={'1.5rem'}
            margin={'1.5rem'}
            marginBottom={'unset'}
            className={classes.contentSubTextContainer}
          >
            <Box className={classes.subTextHeader} margin={'2rem'}>
              {isSuccessful && (
                <>
                  <OutageStatusGreen className={classes.greenCheckIcon} />
                  <Typography
                    variant="h2"
                    className={classes.subTextHeaderTitle}
                  >
                    {t('THANKS_FOR_REPORTING')}
                  </Typography>
                </>
              )}
              {!isSuccessful && (
                <>
                  <HelpIcon className={classes.helpIcon} />
                  <Typography
                    variant="h2"
                    className={classes.subTextHeaderTitle}
                  >
                    {t('HAS_YOUR_POWER_BEEN_RESTORED')}
                  </Typography>
                </>
              )}
            </Box>
            {isSuccessful && (
              <Typography variant="body2" className={classes.textMatterLight}>
                {richT('MY_POWER_IS_ON_CONTENT_SUCCESS')}
              </Typography>
            )}
            {!isSuccessful && (
              <Typography variant="body2" className={classes.textMatterLight}>
                {richT('MY_POWER_IS_ON_CONTENT')}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          className={classes.actions}
          style={isSuccessful ? { justifyContent: 'center' } : undefined}
        >
          <Button
            size="large"
            variant="text"
            className={classes.cancelBtn}
            onClick={handleClose}
          >
            {t('CLOSE')}
          </Button>
          {!isSuccessful && (
            <PGEButton
              size="large"
              color="secondary"
              variant="contained"
              className={classes.lookupBtn}
              onClick={handleSubmit}
            >
              {t('YES_MY_POWER_IS_ON')}
            </PGEButton>
          )}
        </Box>
      </Box>
    </CustomDialog>
  );
}
